import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Check,
  ChevronRight,
  Search,
  Sparkles,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { cohortService, userService } from '../api';
import AppSelect from './ui/AppSelect'

function parseList(res) {
  const data = res?.data ?? res;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

function getDisplayName(student) {
  const name = [student.first_name, student.last_name].filter(Boolean).join(' ').trim();
  return name || student.username || student.email || `Student #${student.id}`;
}

function getInitials(student) {
  const name = getDisplayName(student);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #2F7C7B, #F2B542)',
  'linear-gradient(135deg, #2FA7A1, #2F7C7B)',
  'linear-gradient(135deg, #10B981, #2FA7A1)',
  'linear-gradient(135deg, #F2B542, #EF4444)',
  'linear-gradient(135deg, #0F1F23, #2F7C7B)',
];

function avatarGradient(id) {
  return AVATAR_GRADIENTS[Number(id) % AVATAR_GRADIENTS.length];
}

function StudentAvatar({ student, size = 40 }) {
  return (
    <div
      className="student-picker-avatar"
      style={{
        width: size,
        height: size,
        background: avatarGradient(student.id),
        fontSize: size * 0.36,
      }}
      aria-hidden="true"
    >
      {getInitials(student)}
    </div>
  );
}

function CohortStudentPicker({ cohort, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('approved');
  const [allStudents, setAllStudents] = useState([]);
  const [memberIds, setMemberIds] = useState(new Set());
  const [initialMemberIds, setInitialMemberIds] = useState(new Set());
  const [memberDetails, setMemberDetails] = useState({});

  const loadCohort = useCallback(async () => {
    if (!cohort?.id) return {};
    const detailRes = await cohortService.getCohortDetail(cohort.id);
    const detail = detailRes.data ?? detailRes;
    const members = detail.students_details || [];
    const ids = new Set(members.map((m) => m.id));
    const detailsMap = {};
    members.forEach((m) => { detailsMap[m.id] = m; });
    setMemberIds(new Set(ids));
    setInitialMemberIds(new Set(ids));
    setMemberDetails(detailsMap);
    return detailsMap;
  }, [cohort?.id]);

  const loadStudents = useCallback(async (detailsMap = {}) => {
    const params = { page_size: 200 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (searchTerm.trim()) params.search = searchTerm.trim();

    const studentsRes = await userService.getStudents(params);
    const students = parseList(studentsRes);
    const campusFiltered = cohort?.campus
      ? students.filter((s) => Number(s.campus) === Number(cohort.campus))
      : students;

    setAllStudents(campusFiltered);
    setMemberDetails((prev) => {
      const next = { ...prev, ...detailsMap };
      campusFiltered.forEach((s) => { next[s.id] = next[s.id] || s; });
      return next;
    });
  }, [cohort?.campus, searchTerm, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cohort?.id) return;
      setLoading(true);
      try {
        const detailsMap = await loadCohort();
        if (!cancelled) await loadStudents(detailsMap);
      } catch {
        if (!cancelled) {
          toast.error('Failed to load roster data.');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cohort?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !cohort?.id) return;
    const timer = setTimeout(() => {
      loadStudents().catch(() => toast.error('Failed to refresh student list.'));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, loading, cohort?.id, loadStudents]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const filteredPool = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return allStudents;
    return allStudents.filter((s) => {
      const hay = `${getDisplayName(s)} ${s.email || ''} ${s.username || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allStudents, searchTerm]);

  const selectedMembers = useMemo(
    () => [...memberIds].map((id) => memberDetails[id] || allStudents.find((s) => s.id === id)).filter(Boolean),
    [memberIds, memberDetails, allStudents]
  );

  const pendingAdds = useMemo(
    () => [...memberIds].filter((id) => !initialMemberIds.has(id)),
    [memberIds, initialMemberIds]
  );

  const pendingRemoves = useMemo(
    () => [...initialMemberIds].filter((id) => !memberIds.has(id)),
    [memberIds, initialMemberIds]
  );

  const hasChanges = pendingAdds.length > 0 || pendingRemoves.length > 0;

  const toggleStudent = (student) => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(student.id)) next.delete(student.id);
      else next.add(student.id);
      return next;
    });
    setMemberDetails((prev) => ({ ...prev, [student.id]: student }));
  };

  const selectAllVisible = () => {
    setMemberIds((prev) => {
      const next = new Set(prev);
      filteredPool.forEach((s) => next.add(s.id));
      return next;
    });
    setMemberDetails((prev) => {
      const next = { ...prev };
      filteredPool.forEach((s) => { next[s.id] = s; });
      return next;
    });
  };

  const clearSelection = () => setMemberIds(new Set());

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      if (pendingAdds.length) {
        await cohortService.addStudents(cohort.id, pendingAdds);
      }
      if (pendingRemoves.length) {
        await cohortService.removeStudents(cohort.id, pendingRemoves);
      }
      toast.success(`Roster updated — ${pendingAdds.length} added, ${pendingRemoves.length} removed.`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update cohort roster.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="cohort-picker-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => !saving && onClose()}
      role="presentation"
    >
      <motion.aside
        className="cohort-picker-drawer"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="cohort-picker-title"
      >
        <header className="cohort-picker-header">
          <div>
            <div className="cohort-picker-kicker">
              <Sparkles size={12} />
              Cohort Roster Studio
            </div>
            <h2 id="cohort-picker-title">{cohort.name}</h2>
            <p className="cohort-picker-subtitle">
              Curate your intake group with a visual roster — search, select, and save in one fluid workflow.
            </p>
          </div>
          <button type="button" className="cohort-picker-close" onClick={onClose} disabled={saving} aria-label="Close">
            <X size={22} />
          </button>
        </header>

        <div className="cohort-picker-summary">
          <div className="cohort-picker-summary-stat">
            <span className="cohort-picker-summary-value">{selectedMembers.length}</span>
            <span className="cohort-picker-summary-label">In roster</span>
          </div>
          <div className="cohort-picker-summary-divider" />
          <div className="cohort-picker-summary-stat">
            <span className="cohort-picker-summary-value cohort-picker-summary-add">+{pendingAdds.length}</span>
            <span className="cohort-picker-summary-label">To add</span>
          </div>
          <div className="cohort-picker-summary-divider" />
          <div className="cohort-picker-summary-stat">
            <span className="cohort-picker-summary-value cohort-picker-summary-remove">−{pendingRemoves.length}</span>
            <span className="cohort-picker-summary-label">To remove</span>
          </div>
          <div className="cohort-picker-avatar-stack">
            {selectedMembers.slice(0, 5).map((s, i) => (
              <div key={s.id} className="cohort-picker-stack-item" style={{ zIndex: 10 - i, marginLeft: i ? -10 : 0 }}>
                <StudentAvatar student={s} size={32} />
              </div>
            ))}
            {selectedMembers.length > 5 && (
              <div className="cohort-picker-stack-more">+{selectedMembers.length - 5}</div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="cohort-picker-loading">
            <div className="cohort-picker-spinner" />
            <p>Loading students...</p>
          </div>
        ) : (
          <div className="cohort-picker-split">
            <section className="cohort-picker-panel">
              <div className="cohort-picker-panel-head">
                <h3><Users size={16} /> Available students</h3>
                <div className="cohort-picker-panel-actions">
                  <button type="button" className="cohort-picker-link-btn" onClick={selectAllVisible}>
                    Select visible
                  </button>
                </div>
              </div>

              <div className="cohort-picker-filters">
                <div className="cohort-search-wrap" style={{ flex: 1, minWidth: 0 }}>
                  <Search className="cohort-search-icon" size={16} />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <AppSelect
                  className="cohort-picker-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="all">All</option>
                </AppSelect>
              </div>

              <ul className="cohort-picker-list">
                <AnimatePresence initial={false}>
                  {filteredPool.map((student, idx) => {
                    const selected = memberIds.has(student.id);
                    return (
                      <motion.li
                        key={student.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ delay: Math.min(idx * 0.02, 0.2) }}
                      >
                        <button
                          type="button"
                          className={`student-picker-card${selected ? ' is-selected' : ''}`}
                          onClick={() => toggleStudent(student)}
                        >
                          <StudentAvatar student={student} />
                          <div className="student-picker-card-body">
                            <div className="student-picker-card-name">{getDisplayName(student)}</div>
                            <div className="student-picker-card-email">{student.email}</div>
                          </div>
                          <div className={`student-picker-check${selected ? ' is-checked' : ''}`}>
                            {selected && <Check size={14} strokeWidth={3} />}
                          </div>
                        </button>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
                {filteredPool.length === 0 && (
                  <li className="cohort-picker-list-empty">No students match your search.</li>
                )}
              </ul>
            </section>

            <section className="cohort-picker-panel cohort-picker-panel-roster">
              <div className="cohort-picker-panel-head">
                <h3><UserPlus size={16} /> Selected roster</h3>
                <button type="button" className="cohort-picker-link-btn" onClick={clearSelection}>
                  Clear all
                </button>
              </div>

              <ul className="cohort-picker-roster">
                <AnimatePresence initial={false}>
                  {selectedMembers.map((student) => {
                    const isNew = !initialMemberIds.has(student.id);
                    return (
                      <motion.li
                        key={student.id}
                        layout
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -12, height: 0 }}
                      >
                        <div className="student-roster-chip">
                          <StudentAvatar student={student} size={36} />
                          <div className="student-roster-chip-body">
                            <div className="student-roster-chip-name">{getDisplayName(student)}</div>
                            {isNew && <span className="student-roster-badge-new">New</span>}
                          </div>
                          <button
                            type="button"
                            className="student-roster-remove"
                            onClick={() => toggleStudent(student)}
                            aria-label={`Remove ${getDisplayName(student)}`}
                          >
                            <UserMinus size={16} />
                          </button>
                        </div>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
                {selectedMembers.length === 0 && (
                  <li className="cohort-picker-roster-empty">
                    <Users size={32} className="text-muted" />
                    <p>Select students from the pool to build your cohort roster.</p>
                  </li>
                )}
              </ul>
            </section>
          </div>
        )}

        <footer className="cohort-picker-footer">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary cohort-picker-save"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving roster...' : hasChanges ? `Save changes (${pendingAdds.length + pendingRemoves.length})` : 'Done'}
            {!saving && <ChevronRight size={18} style={{ marginLeft: 6 }} />}
          </button>
        </footer>
      </motion.aside>
    </motion.div>
  );
}

export default CohortStudentPicker;
